import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, Input, OnInit, Optional, Output, Self, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, fromEvent, map, merge, scan } from 'rxjs';

interface NumberInputState{
  value: number;
  min: number;
  max: number;
}

/**
 * This component is used to create a number input field with increment and decrement buttons, can be used in forms
 * @example 
 *  <fgbcl-number-input formControlName="quantity"></fgbcl-number-input>
 *  using this as a form control in a form group is not yet tested, but it should work
 */
@Component({
  selector: 'fgbcl-number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss']
})
export class NumberInputComponent implements OnInit, AfterViewInit, ControlValueAccessor{
  @Input() isActive: boolean = true;
  @Input() minMessage: string;
  @Input() maxMessage: string;
  @Input() theme?: string;
  @Input() unitLabel?: string;
  @Input() incrementValue: number;
  @Input() limitValueToRange: boolean | undefined = true;
  @Input() unlimitedLimit: number = 100; 
  @Input() set minValue(value: number){
    this._minValue = value;
    this.minimum.next(value);
  };
  @Input() set maxValue(value: number){
    this._maxValue = value;
    this.maximum.next(value);
  }
  @Input() set amount(value: number){
    this.innerValue = value;
    this.amountSubject.next(value);
  }
  @Output() amountChange = new EventEmitter();
  @Input() outOfRange: boolean = false;
  @ViewChild('input') inputText: ElementRef<HTMLInputElement>;
  @Output() outOfRangeChange = new EventEmitter<boolean>();
  @Input() showErrorMessage: boolean = true; // For the sake of backward compatibility added this input, should be removed
   // There is no point showing the error message if the input is changed dynamically to limit the value
  // Suggested to use validator instead to handle the error message(s), it is also hard to maintain the error message(s) in the parent component,
  // It's better to use this as a form control and use validator, it's hard to maintain this as ngModel, two-way binding can cause issues if not handled properly
  @Output() errorChange = new EventEmitter<string>(); 
  private destroyRef = inject(DestroyRef);
  private _onChangeCallback: (_: number) => void;
  private _onTouchedCallback: () => void;
  private _minValue: number;
  private _maxValue: number;
  control: AbstractControl | any;
  // Added innerValue so it won't have any unexpected behavior, previously it was doing two-way binding
  // inside the component, and doing two-way binding in the parent, which is not recommended, might
  // cause unexpected behavior
  innerValue: number;
  increment = new Subject<number>();
  decrement = new Subject<number>();
  minimum = new Subject<number>();
  maximum = new Subject<number>();
  amountSubject = new Subject<number>();

  showMinMessage = false;
  showMaxMessage = false;

  constructor(@Optional() @Self() private ngControl: NgControl) { 
    if( this.ngControl ) this.ngControl.valueAccessor = this;
    // Set private defaults
    this._onChangeCallback = (_: number) => {};
    this._onTouchedCallback = () => {};
  }

  ngOnInit(): void {
    if( this.ngControl ){
      this.control = this.ngControl.control;
      this.control.updateValueAndValidity();
    }
    if (!this.innerValue) {
      this.amount = this.minValue;
    }
  }

  ngAfterViewInit(): void {
    // Merge events
    merge(
      // Input changes
      this.onMinimumChanged.pipe(map(this.minimumHandler)),
      this.onMaximumChanged.pipe(map(this.maximumHandler)),
      this.onAmountChanged.pipe(map(this.amountHandler)),
      // Event changes, button click and input event(s)
      this.onIncrementChanged.pipe(map(this.incrementHandler)),
      this.onDecrementChanged.pipe(map(this.decrementHandler)),
      fromEvent(this.inputText.nativeElement, 'input')
      .pipe(
        map(this.inputHandler),
        debounceTime(300),
        distinctUntilChanged()
      )
    ).pipe(
      // Run event function and validate the amount
      scan((state, eventFn) => this.validateAmountHandler(eventFn(state)), { value: this.innerValue, min: this.minValue, max: this.maxValue}),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.writeValue(data.value);
    });
  }

  /** Validate range */
  validateAmountHandler(state: NumberInputState): NumberInputState{
      this.setDefaults();
      this.errorChange.emit('');

      // If limit then max is set to 0, set the value to 1
      if( this.limitValueToRange && this.maxValue === 0 ){
        return { ...state, value: 1 };
      }

      if( this.limitValueToRange === undefined && state.value > state.max){
        this.showMaxMessage = true;
        this.errorChange.emit(this.maxMessage);
      }

      if( state.value < state.min ){
          this.showMinMessage = true;
          this.errorChange.emit(this.minMessage);
          return { ...state, value: state.min };
      }else if(this.limitValueToRange && state.value > state.max){
          this.showMaxMessage = true;
          this.errorChange.emit(this.maxMessage);
          return { ...state, value: state.max };
      }else if( this.limitValueToRange === false ){
        // There is a max value set for unlimited item, should be handled in the api
        // currently set to 100 as default
        if( state.value > this.unlimitedLimit ){
          return { ...state, value: this.unlimitedLimit };
        }
      }
      return { ...state, value: state.value };
  }

  /**
   * Set defaults to false
   */
  setDefaults(): void{
    this.showMaxMessage = false;
    this.showMinMessage = false;
  }

  /**
   * Handles the input event
   */
  private inputHandler(event: any){
    return (state: NumberInputState) => ({ ...state, value: +event.target.value || 1 });
  }

  /**
   * Handles the amount event
   */
  private amountHandler(event: any){
    return (state: NumberInputState) => ({ ...state, value: event === 0 ? 1 : event });
  }

  /**
   * Handles increment event, increment by the increment value
   */
  private incrementHandler(incrementValue: number){
    return (state: NumberInputState) => ({ ...state, value:  state.value + incrementValue});
  }

  /**
   * Handles decrement event, decrement by the increment value
   */
  private decrementHandler(incrementValue: number){
    return (state: NumberInputState) => ({ ...state, value: state.value - incrementValue });
  }

  /**
   * Maximum @Input event handler
   */
  private maximumHandler(max: number){
    return (state: NumberInputState) => ({ ...state, max });
  }

  /**
   * Minimum @Input event handler
   */
  private minimumHandler(min: number){
    return (state: NumberInputState) => ({ ...state, min});
  }

  get onIncrementChanged(){
    return this.increment.asObservable();
  }

  get onDecrementChanged(){
    return this.decrement.asObservable();
  }

  get onMinimumChanged(){
    return this.minimum.asObservable();
  }

  get onMaximumChanged(){
    return this.maximum.asObservable();
  }

  get onAmountChanged(){
    return this.amountSubject.asObservable();
  }

  get maxValue(): number{
    return this._maxValue;
  }

  get minValue(): number{
    return this._minValue;
  }

  writeValue(value: number): void {
    this.innerValue = value;
    this._updateValue();
  }

  /**
   * Update the value of the input
   */
  private _updateValue(): void{
    // Update the amount value
    this._onChangeCallback(this.innerValue);
    // Mark the control as touched
    this._onTouchedCallback();
    // Notify the parent component of the change
    this.amountChange.emit(this.innerValue);

    const isNumberOutOfRange = this.innerValue < this.minValue || this.innerValue > this.maxValue;
    this.outOfRangeChange.emit(isNumberOutOfRange);
  }

  /**
   * Register on change function
   */
  registerOnChange(fn: any): void {
    this._onChangeCallback = fn;
  }

  /**
   * Register on touched function
   */
  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  /** Prevents using decimal in the number input */
  onNumberKeydown(event: KeyboardEvent): void {
    if (event.key === '.') event.preventDefault();
  }

  // @Input() unlimited: boolean = false;
  // showMinMessage = false;
  // showMaxMessage = false;

  // constructor() {}

  // ngOnInit() {
  //   if (!this.amount) {
  //     this.amount = this.minValue;
  //   }
  //   if( this.unlimited ){
  //     this.minValue = 1;
  //   }
  // }

  // ngAfterViewInit(): void {
    
  //   fromEvent(this.inputText.nativeElement, 'input')
  //     .pipe(
  //       map((event: any) => +event.target.value || 1),
  //       debounceTime(300),
  //       distinctUntilChanged(),
  //       takeUntilDestroyed(this.destroyRef)
  //     ).subscribe((value) => {
  //       this.amount = value;
  //       if( !this.unlimited ){
  //         this.amount = this.validateAmount(value, true);
  //       }else{
  //         if( this.amount > this.unlimitedLimit ){
  //           this.amount = this.unlimitedLimit;
  //         }
  //       }
  //       this.amountChange.emit(this.amount);
  //       this.outOfRangeChange.emit(this.isNumberOutOfRange());
  //     });
  // }

  // increment() {
  //   let amount = this.amount;
  //   if (this.amount < this.minValue) {
  //     amount = this.minValue;
  //   } else if (this.amount + this.incrementValue <= this.maxValue || this.unlimited) {
  //     amount = this.amount + this.incrementValue;
  //   }
  //   this.showMinMessage = false;
  //   this.amount = this.validateAmount(amount, false);
  //   this.amountChange.emit(this.amount);
  // }

  // decrement() {
  //   let amount = this.amount;
  //   if (this.amount > this.maxValue && !this.unlimited) {
  //     amount = this.maxValue;
  //   } else if (this.amount - this.incrementValue >= this.minValue) {
  //     amount = this.amount - this.incrementValue;
  //   }
  //   this.showMaxMessage = false;
  //   this.amount = this.validateAmount(amount, false);
  //   this.amountChange.emit(this.amount);
  // }

  // isNumberOutOfRange(): boolean {
  //   return this.amount < this.minValue || this.amount > this.maxValue;
  // }

  // /** Prevents using decimal in the number input */
  // onNumberKeydown(event: KeyboardEvent): void {
  //   if (event.key === '.') event.preventDefault();
  // }

  // /** Valdiates the amount compared to the min and max value inputs and returns the correct amount after comparison */
  // private validateAmount(x: number, showMessages: boolean): number {
  //   if (showMessages) {
  //     this.showMaxMessage = false;
  //     this.showMinMessage = false;
  //   }
  //   if (x > this.maxValue) {
  //     if (this.limitValueToRange) {
  //       x = this.maxValue;
  //     }
  //     if (showMessages) {
  //       if( !this.unlimited ){
  //         this.showMaxMessage = true;
  //       }
  //     }
  //   } else if (x < this.minValue) {
  //     if (this.limitValueToRange) {
  //       x = this.minValue;
  //     }
  //     if (showMessages) {
  //       this.showMinMessage = true;
  //     }
  //   } else {
  //     this.showMaxMessage = false;
  //     this.showMinMessage = false;
  //   }
  //   return x;
  // }
}
